import { useQuery } from '@tanstack/react-query';
import type { ChangeKind, ChangelogEntry } from '@esa/shared';
import { api } from '../../lib/api.js';
import { Badge, Panel, type Tone } from '../../ui/index.js';

/**
 * Configuration → Platform → Changelog. What changed, release by release,
 * in plain words — for the people running Tributary, not for developers.
 */

const KIND: Record<ChangeKind, { label: string; tone: Tone }> = {
  new: { label: 'new', tone: 'ok' },
  improved: { label: 'improved', tone: 'info' },
  fixed: { label: 'fixed', tone: 'warn' },
};

const day = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

export function ChangelogPanel() {
  const q = useQuery({
    queryKey: ['platform-changelog'],
    queryFn: () => api.get<{ entries: ChangelogEntry[] }>('/api/v1/platform/changelog'),
  });

  return (
    <Panel title="Changelog" subtitle="What changed in Tributary, newest first.">
      {q.isLoading && <p className="text-xs text-ink-tertiary">Loading…</p>}
      {q.error && <p className="text-xs text-danger">The changelog could not be loaded.</p>}
      <ol className="space-y-5">
        {(q.data?.entries ?? []).map((e, i) => (
          <li key={`${e.date}-${e.pr ?? 'next'}-${i}`}>
            <div className="flex flex-wrap items-baseline gap-x-2">
              <h3 className="text-sm font-semibold text-ink">{e.title}</h3>
              <span className="text-2xs text-ink-tertiary">
                {day(e.date)}
                {e.pr ? ` · #${e.pr}` : ' · not yet released'}
              </span>
            </div>
            <ul className="mt-1.5 space-y-1">
              {e.items.map((it, j) => (
                <li key={j} className="grid grid-cols-[4.5rem_1fr] items-baseline gap-2 text-xs leading-relaxed text-ink-secondary">
                  <span>
                    <Badge tone={KIND[it.kind].tone}>{KIND[it.kind].label}</Badge>
                  </span>
                  <span>{it.text}</span>
                </li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </Panel>
  );
}
